<template>
  <div :class="`order-details-hero ${orderHeader.status.toLowerCase()}`">
    <NavBar :transparent="!isCanceled">
      <template #left>
        <BackButton @click="$emit('close')" />
      </template>
    </NavBar>
    <div v-if="!isCanceled" class="order-details-hero__video-container">
      <video
        class="order-details-hero__video"
        autoplay
        muted
        loop
        playsinline
        aria-hidden="true"
        role="presentation"
      >
        <source :src="videoSourceByStatus" type="video/webm" />
      </video>
    </div>
    <div class="order-details-hero__content">
      <template v-if="isCanceled">
        <HelperViewStatus
          class="order-details-hero__canceled"
          data-testid="order-details-hero-canceled"
          status="negative"
          :title="orderHeader.title"
          :description="orderHeader.description"
        />
      </template>
      <template v-else>
        <div class="order-details-hero__head">
          <div class="order-details-hero__head__value">
            <Heading class="order-details-hero__head__title" :level="3">
              {{ orderHeader.title }}
            </Heading>
            <button
              v-if="orderItem.details.fees"
              class="order-details-hero__head__button"
              @click="openFeesDetails(orderItem.details.fees)"
            >
              <BaseText size="p4" :color="NebraskaColors.elementOverImage">
                {{ orderHeader.value }}
              </BaseText>
              <BaseIcon
                icon="EA0170"
                width="20px"
                height="20px"
                :colors="NebraskaColors.elementOverImage"
              />
            </button>
            <BaseText v-else size="p4" :color="NebraskaColors.elementOverImage">
              {{ orderHeader.value }}
            </BaseText>
          </div>
          <InitialAvatarIcon
            class="order-details-hero__head__icon"
            size="medium"
            :icon="orderItem.icon"
          />
        </div>
        <BaseText
          class="order-details-hero__description"
          size="p5"
          :color="NebraskaColors.elementAlpha"
        >
          {{ orderHeader.description }}
        </BaseText>
        <section v-if="orderHeader.progress?.showProgress">
          <ProgressBar
            class="order-details-hero__progress"
            :progress-color="NebraskaColors.elementOverImage"
            :progress-background="NebraskaColors.cinza25"
            :progress="orderHeader.progress?.value"
            :height="4"
          />
          <section class="order-details-hero__footer">
            <div>
              <BaseText size="p6" :color="NebraskaColors.elementOverImage">
                {{ orderHeader.progress?.startPoint.value }}
              </BaseText>
              <BaseText size="p6" :color="NebraskaColors.elementAlpha">
                {{ orderHeader.progress?.startPoint.label }}
              </BaseText>
            </div>
            <div class="order-details-hero__footer__right">
              <BaseText size="p6" :color="NebraskaColors.elementOverImage">
                {{ orderHeader.progress?.endPoint.value }}
              </BaseText>
              <BaseText size="p6" :color="NebraskaColors.elementAlpha">
                {{ orderHeader.progress?.endPoint.label }}
              </BaseText>
            </div>
          </section>
        </section>
        <section v-else class="order-details-hero__info">
          <BaseText size="p6" :color="NebraskaColors.elementOverImage">
            {{ orderHeader.progress?.startPoint.label }}
          </BaseText>
          <BaseIcon
            width="18px"
            height="18px"
            icon="EA0040"
            :colors="NebraskaColors.elementOverImage"
          />
          <BaseText size="p6" :color="NebraskaColors.elementOverImage">
            {{ orderHeader.progress?.endPoint.label }}
          </BaseText>
        </section>
      </template>
    </div>

    <FeesDetails
      :is-open="isFeesDetailsOpen"
      :fees="feesDetails"
      @close="isFeesDetailsOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  NavBar,
  HelperViewStatus,
  Heading,
  BaseText,
  InitialAvatarIcon,
  ProgressBar,
  BaseIcon
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { FeesDetails } from './components';

import {
  IOrderItem,
  EOrderStatus,
  IFeesDetails
} from '@/modules/wealth/services/portfolio-orders';

interface Props {
  orderItem: IOrderItem;
}

const props = defineProps<Props>();

const isFeesDetailsOpen = ref(false);
const feesDetails = ref<IFeesDetails[]>([]);

const isCanceled = computed(() => {
  return orderHeader.value.status === EOrderStatus.CANCELED;
});

const orderHeader = computed(() => {
  return props.orderItem.details.header;
});

const videoSourceByStatus = computed(() => {
  const statusMapper: { [key: string]: string } = {
    [EOrderStatus.FINISHED]: require('@/assets/videos/green-bg-animated.webm'),
    [EOrderStatus.PROCESSING]: require('@/assets/videos/blue-bg-animated.webm')
  };

  return statusMapper[orderHeader.value.status];
});

function openFeesDetails(fees: IFeesDetails[]) {
  feesDetails.value = fees;
  isFeesDetailsOpen.value = true;
}
</script>

<style scoped lang="less">
.processing {
  background-image: linear-gradient(180deg, #243f6f, #0c1625);
}

.finished {
  background-image: linear-gradient(180deg, #214332, #0c1810);
}

.order-details-hero {
  width: 100%;
  position: relative;
  overflow: hidden;

  .navbar-container {
    border-bottom: none;
  }

  &__video-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  &__video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__content {
    z-index: 1;
    position: relative;
    padding: @size-spacing-x800 @size-spacing-x1200;
  }

  &__canceled {
    width: 100%;
    margin: 0 auto;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: @size-spacing-x800;

    &__value {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    &__title {
      color: @element-over-image;
      margin-bottom: @size-spacing-x150;
    }

    &__button {
      display: flex;
      padding: 0;
      gap: @size-spacing-x150;
    }

    &__icon {
      color: @element-over-image;
      background-color: @cinza-25;
    }
  }

  &__description,
  &__progress {
    margin-bottom: @size-spacing-x400;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;

    > div {
      display: flex;
      flex-direction: column;
      gap: @size-spacing-x150;
    }

    &__right {
      align-items: flex-end;
    }
  }

  &__info {
    display: flex;
    align-items: center;
    gap: @size-spacing-x350;
  }
}
</style>
